/**
 * AuthService
 * --------------------------------------------
 * --------------------------------------------
 *
 * Controlador que tiene el endpoint de auth y generar el token.
 *
 * Endpoints:
 * ------------------
 * 1.  POST    ->  {ENDPOINT_PATH}login
 *
 */

import axios from "axios";
import decode from "jwt-decode";

let AUTH_TOKEN_KEY = "authToken";
const ENDPOINT_PATH = "http://urbanbrain.siarq.com:8080/auth/";

/** 1. Endpoint de autenticacion
 * ------------------------------------------------------------------------------
 * Recibe el token, el userName, los clusters del usuario y si es premium o no.
 *
 * url: {ENDPOINT_PATH}login
 *
 * method: GET
 *
 * type: JSON
 *
 * @param {String} userName
 * @param {String} password
 */
export function loginUser(userName, password) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await axios({
        url: `${ENDPOINT_PATH}login`,
        method: "POST",
        data: {
          email: userName,
          password: password,
          grant_type: "password",
        },
      });

      setAuthToken(res.data.token);
      resolve(res.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}

/**
 * Llama una función que añade el token al header y lo guarda en sessionStorage
 *
 * @function clearAuthToken()
 *
 */
export function logoutUser() {
  clearAuthToken();
}

/**
 * Añade el token al header y lo guarda en sessionStorage
 *
 * @param {String} token - el input token
 *
 */
export function setAuthToken(token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Obtiene el token del sessionStorage a partir del AUTH_TOKEN_KEY
 *
 * @returns {String} Retorna el token del sessionStorage
 */
export function getAuthToken() {
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

export function clearAuthToken() {
  axios.defaults.headers.common["Authorization"] = "";
  sessionStorage.clear();
}

/**
 * Comprueba si el usuario está logueado
 *
 * @returns {Boolean} Retorna true o false si el usuario está logueado.
 */
export function isLoggedIn() {
  let authToken = getAuthToken();
  return !!authToken && !isTokenExpired(authToken);
}

/**
 * Si está logueado, retorna el token decodificado
 *
 * @returns {String} retorna el token decodificado
 */
export function getUserInfo() {
  if (isLoggedIn()) {
    return decode(getAuthToken());
  }
}

/**
 * Obtiene la fecha de expiración del token
 *
 * @param {String} encodedToken - el input token codificaco
 *
 * @returns {Date} La fecha de expiración del token
 */
function getTokenExpirationDate(encodedToken) {
  let token = decode(encodedToken);
  if (!token.exp) {
    return null;
  }

  let date = new Date(0);
  date.setUTCSeconds(token.exp);

  return date;
}

/**
 * Devuelve  si el token está expirado o no
 *
 * @param {String} token - el input token
 *
 * @returns {Boolean} Retorna true o false si ha expirado o no
 */
function isTokenExpired(token) {
  let expirationDate = getTokenExpirationDate(token);
  return expirationDate < new Date();
}
